import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  getFormulario,
  crearPaso, editarPaso, eliminarPaso, reordenarPasos,
  crearCampo, editarCampo, eliminarCampo, reordenarCampos,
} from '../api/muestrasApi'
import type { PasoCreatePayload, CampoCreatePayload } from '../types'

export const formBuilderKeys = {
  all: ['muestras-form-builder'] as const,
  formulario: (campaniaId: number) => [...formBuilderKeys.all, campaniaId] as const,
}

export function useFormulario(campaniaId: number) {
  return useQuery({
    queryKey: formBuilderKeys.formulario(campaniaId),
    queryFn: () => getFormulario(campaniaId),
    enabled: campaniaId > 0,
  })
}

export function useCrearPaso(campaniaId: number) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: PasoCreatePayload) => crearPaso(campaniaId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: formBuilderKeys.formulario(campaniaId) })
      toast.success('Paso creado')
    },
  })
}

export function useEditarPaso(campaniaId: number) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ pasoId, payload }: { pasoId: number; payload: Partial<PasoCreatePayload> }) =>
      editarPaso(campaniaId, pasoId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: formBuilderKeys.formulario(campaniaId) })
      toast.success('Paso actualizado')
    },
  })
}

export function useEliminarPaso(campaniaId: number) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (pasoId: number) => eliminarPaso(campaniaId, pasoId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: formBuilderKeys.formulario(campaniaId) })
      toast.success('Paso eliminado')
    },
  })
}

export function useReordenarPasos(campaniaId: number) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (orden: number[]) => reordenarPasos(campaniaId, orden),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: formBuilderKeys.formulario(campaniaId) })
    },
  })
}

export function useCrearCampo(campaniaId: number) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: CampoCreatePayload) => crearCampo(campaniaId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: formBuilderKeys.formulario(campaniaId) })
      toast.success('Campo creado')
    },
  })
}

export function useEditarCampo(campaniaId: number) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ campoId, payload }: { campoId: number; payload: Partial<CampoCreatePayload> }) =>
      editarCampo(campaniaId, campoId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: formBuilderKeys.formulario(campaniaId) })
      toast.success('Campo actualizado')
    },
  })
}

export function useEliminarCampo(campaniaId: number) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (campoId: number) => eliminarCampo(campaniaId, campoId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: formBuilderKeys.formulario(campaniaId) })
      toast.success('Campo eliminado')
    },
  })
}

export function useReordenarCampos(campaniaId: number) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (orden: number[]) => reordenarCampos(campaniaId, orden),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: formBuilderKeys.formulario(campaniaId) })
    },
  })
}
